import { useMemo, useState } from "react";
import { router, useForm } from "@inertiajs/react";
import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import EmptyState from "@/components/ui/EmptyState";
import {
  Checkbox,
  Field,
  Input,
  Select,
  Textarea,
} from "@/components/ui/Field";

type Payout = {
  id: number;
  amount_cents: number;
  sent_on: string;
  method: string;
  method_label: string;
  reference: string | null;
  note: string | null;
  sent_by: string | null;
};

type PayoutRow = Payout & { event_id: number; event_name: string };

type FundableEvent = {
  id: number;
  name: string;
  location: string;
  starts_at: string | null;
  organizer: string | null;
  country: string | null;
  rate_cents: number;
  rate_country: string | null;
  verified_rsvps: number;
  earned_cents: number;
  rsvp_cents: number;
  // Which grant this row is on. "sites" is the international model: paid ahead
  // of the day per approved site handed in before the start, with RSVPs
  // playing no part, so the signup count is not what the total reconciles to.
  funding_model: "rsvps" | "sites";
  prefunding_cents: number;
  funded_sites: number;
  pending_sites: number;
  rate_per_site_cents: number;
  // Where an international payment actually goes. It does not pass through HCB,
  // so these are the payment details rather than contact trivia.
  organizer_email: string | null;
  organizer_phone: string | null;
  organizer_slack_id: string | null;
  coding_cents: number;
  paid_cents: number;
  outstanding_cents: number;
  venue_confirmed: boolean;
  hcb_ready: boolean;
  hcb_blockers: string[];
  hcb_url: string | null;
  hcb_stage: "blocked" | "ready_to_request" | "awaiting_url" | "ready_to_fund";
  hcb_stage_label: string;
  payouts: Payout[];
};

type Totals = {
  earned_cents: number;
  paid_cents: number;
  outstanding_cents: number;
  awaiting_count: number;
};

function money(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <div className="border-line bg-surface flex min-w-40 flex-1 flex-col gap-1 border-2 p-4">
      <span
        className={`font-display text-heading font-bold ${tone ?? "text-ink"}`}
      >
        {value}
      </span>
      <span className="text-caption text-ink-muted">{label}</span>
    </div>
  );
}

// One event's funding state, with the form to log a transfer against it. The
// amount defaults to what's outstanding, because that's the transfer HQ is
// almost always about to make.
function EventRow({
  event,
  methods,
}: {
  event: FundableEvent;
  methods: { value: string; label: string }[];
}) {
  const [open, setOpen] = useState(false);
  const { data, setData, post, processing, errors, reset, transform } = useForm(
    {
      amount_dollars: (event.outstanding_cents / 100).toFixed(2),
      sent_on: new Date().toISOString().slice(0, 10),
      // An international payment never goes through HCB, so defaulting it to
      // an HCB method would mislabel every row on the way in.
      method:
        event.funding_model === "sites"
          ? "direct"
          : (methods[0]?.value ?? "hcb_transfer"),
      reference: "",
      note: "",
    },
  );

  const settled = event.outstanding_cents === 0;
  const bySites = event.funding_model === "sites";
  // A US transfer lands in an HCB account, so no account means no form and the
  // server refuses it too. An international one is pre-funding sent straight to
  // the organizer and never touches HCB, so gating it on an HCB URL would block
  // every payment the site grant exists to make.
  const payable = bySites || Boolean(event.hcb_url);

  return (
    <div className="border-line/20 flex flex-col gap-3 border-b py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1 basis-56">
          <p className="text-body text-ink truncate font-bold">{event.name}</p>
          <p className="text-caption text-ink-soft truncate">
            {event.location}
            {event.starts_at && ` · ${event.starts_at}`}
            {event.organizer && ` · ${event.organizer}`}
          </p>
          {!bySites && event.hcb_url && (
            <a
              className="text-caption text-link underline"
              href={event.hcb_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {event.hcb_url.replace("https://", "")}
            </a>
          )}
          {/* The payment details for a city paid directly. Selectable text
              rather than a mailto, because this gets pasted into whatever rail
              is being used to send the money. */}
          {bySites && (
            <p className="text-caption text-ink-muted">
              {event.organizer_email ? (
                <span className="text-ink font-literal select-all">
                  {event.organizer_email}
                </span>
              ) : (
                <span className="text-sunset-orange font-bold">no email</span>
              )}
              {" · "}
              {event.organizer_phone ? (
                <span className="text-ink font-literal select-all">
                  {event.organizer_phone}
                </span>
              ) : (
                <span className="text-sunset-orange font-bold">no phone</span>
              )}
            </p>
          )}
        </div>

        <div className="text-caption text-ink-muted shrink-0 text-right">
          <p>
            {event.funding_model === "sites" ? (
              <>
                {event.funded_sites}{" "}
                {event.funded_sites === 1 ? "site" : "sites"} ×{" "}
                {money(event.rate_per_site_cents)}
                {event.pending_sites > 0 &&
                  ` (${event.pending_sites} in review)`}
              </>
            ) : (
              <>
                {event.verified_rsvps} × {money(event.rate_cents)}
                {event.rate_country && ` (${event.rate_country})`}
              </>
            )}
            {event.coding_cents > 0 && ` + ${money(event.coding_cents)} coding`}
          </p>
          <p>
            earned {money(event.earned_cents)} · sent {money(event.paid_cents)}
          </p>
        </div>

        {/* HCB blockers are about getting an account open, which an
            international city does not need. */}
        {!bySites && !payable && (
          <Badge tone="danger">
            {event.hcb_blockers.length > 0
              ? `Needs ${event.hcb_blockers.join(", ")}`
              : event.hcb_stage_label}
          </Badge>
        )}

        {settled ? (
          <Badge tone="success">Settled</Badge>
        ) : (
          // The number to send, said as an instruction rather than a balance.
          // This is the one thing the page exists to answer.
          <span className="border-line bg-goldenrod font-display text-body text-ink shrink-0 border-2 px-3 py-1 font-bold">
            send {money(event.outstanding_cents)}
          </span>
        )}

        <Button
          size="sm"
          variant={settled ? "ghost" : "primary"}
          disabled={!payable}
          title={
            payable
              ? undefined
              : "No HCB account on file yet, so there's nowhere to send it."
          }
          onClick={() => setOpen(!open)}
        >
          {open ? "Close" : "Log a transfer"}
        </Button>
      </div>

      {event.payouts.length > 0 && (
        <div className="flex flex-col gap-1 pl-1">
          {event.payouts.map((payout) => (
            <div
              key={payout.id}
              className="text-caption text-ink-muted flex flex-wrap items-center gap-2"
            >
              <span className="text-ink font-bold">
                {money(payout.amount_cents)}
              </span>
              <span>
                {payout.sent_on} · {payout.method_label}
                {payout.reference && ` · ${payout.reference}`}
                {payout.sent_by && ` · logged by ${payout.sent_by}`}
              </span>
              {payout.note && <span className="italic">“{payout.note}”</span>}
              <button
                type="button"
                className="text-link underline"
                onClick={() => {
                  if (
                    confirm(
                      `Remove the ${money(payout.amount_cents)} entry? The audit trail keeps it.`,
                    )
                  )
                    router.delete(`/admin/payouts/${payout.id}`, {
                      preserveScroll: true,
                    });
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {open && payable && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            transform((d) => ({
              amount_cents: Math.round(
                parseFloat(d.amount_dollars || "0") * 100,
              ),
              sent_on: d.sent_on,
              method: d.method,
              reference: d.reference,
              note: d.note,
            }));
            post(`/admin/events/${event.id}/payouts`, {
              preserveScroll: true,
              onSuccess: () => {
                reset();
                setOpen(false);
              },
            });
          }}
          className="border-line bg-surface-plain flex flex-wrap items-end gap-3 border-2 p-3"
        >
          <Field label="Amount sent" error={errors.amount_cents}>
            <Input
              type="number"
              step="0.01"
              value={data.amount_dollars}
              onChange={(e) => setData("amount_dollars", e.target.value)}
              className="w-28"
              required
            />
          </Field>

          <Field label="Sent on" error={errors.sent_on}>
            <Input
              type="date"
              value={data.sent_on}
              onChange={(e) => setData("sent_on", e.target.value)}
              required
            />
          </Field>

          <Field label="How" error={errors.method}>
            <Select
              value={data.method}
              onChange={(e) => setData("method", e.target.value)}
            >
              {methods.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Reference"
            hint="HCB transaction or link."
            error={errors.reference}
          >
            <Input
              value={data.reference}
              onChange={(e) => setData("reference", e.target.value)}
              className="w-48"
            />
          </Field>

          <Field label="Note" error={errors.note}>
            <Textarea
              rows={1}
              value={data.note}
              onChange={(e) => setData("note", e.target.value)}
              className="w-56"
            />
          </Field>

          <Button type="submit" disabled={processing}>
            {processing ? "Logging…" : "Log it"}
          </Button>
        </form>
      )}
    </div>
  );
}

export default function AdminPayouts({
  events,
  recent,
  totals,
  methods,
}: {
  events: FundableEvent[];
  recent: PayoutRow[];
  totals: Totals;
  methods: { value: string; label: string }[];
}) {
  const [owingOnly, setOwingOnly] = useState(true);
  const [query, setQuery] = useState("");

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((e) => {
      if (owingOnly && e.outstanding_cents === 0) return false;
      if (!q) return true;
      return (
        e.name.toLowerCase().includes(q) || e.location.toLowerCase().includes(q)
      );
    });
  }, [events, owingOnly, query]);

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          eyebrow="Admin"
          title="Funding"
          subtitle="Money moves in HCB, not here. This is where what was sent gets written down, so organizers can see it and nobody gets paid twice."
        />

        <div className="flex flex-wrap gap-3">
          <Stat
            label="Earned across all events"
            value={money(totals.earned_cents)}
          />
          <Stat
            label="Sent so far"
            value={money(totals.paid_cents)}
            tone="text-leaf"
          />
          <Stat
            label={`Still owed · ${totals.awaiting_count} ${totals.awaiting_count === 1 ? "event" : "events"}`}
            value={money(totals.outstanding_cents)}
            tone="text-danger"
          />
        </div>

        <Panel title={`funding-queue.csv - ${shown.length}`}>
          <div className="flex flex-col">
            <div className="flex flex-wrap items-center gap-3 pb-3">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter by event or city"
                className="max-w-xs"
                aria-label="Filter events"
              />
              <label className="text-caption text-ink-muted flex items-center gap-2">
                <Checkbox
                  checked={owingOnly}
                  onChange={(e) => setOwingOnly(e.target.checked)}
                />
                Only events that are owed something
              </label>
            </div>

            {shown.length === 0 ? (
              <EmptyState
                title={owingOnly ? "Everyone's paid" : "Nothing to fund yet"}
                description={
                  owingOnly
                    ? "Nothing is owed right now."
                    : "Events show up here once they're approved and have either confirmed a venue or earned something by shipping."
                }
              />
            ) : (
              shown.map((event) => (
                <EventRow key={event.id} event={event} methods={methods} />
              ))
            )}
          </div>
        </Panel>

        {recent.length > 0 && (
          <Panel title="recent-transfers.csv">
            <div className="flex flex-col">
              {recent.map((payout) => (
                <div
                  key={payout.id}
                  className="border-line/20 flex flex-wrap items-center gap-3 border-b py-2 last:border-b-0"
                >
                  <span className="text-body text-ink w-24 shrink-0 font-bold">
                    {money(payout.amount_cents)}
                  </span>
                  <span className="text-caption text-ink min-w-0 flex-1 truncate">
                    {payout.event_name}
                  </span>
                  <span className="text-caption text-ink-soft shrink-0">
                    {payout.sent_on} · {payout.method_label}
                    {payout.sent_by && ` · ${payout.sent_by}`}
                  </span>
                </div>
              ))}
            </div>
          </Panel>
        )}
      </div>
    </Layout>
  );
}
